```vue
<template>
  <div class="ukg-test-container ukg-page">

    <!-- Header row -->
    <div class="page-header-row">
      <div class="page-header-left"></div>

      <h1 class="page-heading">UKG READY API TEST</h1>

      <div class="page-header-right"></div>
    </div>

    <div class="main-content">

      <!-- LEFT CONTROLS -->
      <div class="left-side-section">

        <div class="panel info-panel">
          <div class="panel-title">Connection and VOE Testing</div>

          <div class="small-muted">
            Test Pablo's UKG connection, retrieve an employee sample, or test
            the new VOE routes for a specific employee.
          </div>
        </div>

        <button
          class="plan-it-btn"
          :disabled="loading"
          @click="runTest"
        >
          {{ loadingAction === "connection" ? "Testing..." : "Test UKG API" }}
        </button>

        <button
          class="plan-it-btn"
          :disabled="loading"
          @click="runEmployeeSample"
        >
          {{ loadingAction === "employees" ? "Loading..." : "Get Employees" }}
        </button>

        <div class="panel employee-panel">
          <div class="panel-title">VOE Employee</div>

          <label class="field-label" for="employee-id">
            Employee ID
          </label>

          <input
            id="employee-id"
            v-model.trim="employeeId"
            class="employee-input"
            type="text"
            autocomplete="off"
            placeholder="Example: 1348"
            :disabled="loading"
            @keyup.enter="runVoe"
          />

          <div class="small-muted employee-help">
            The production VOE route should return data only when the
            employee's pay type is HOURLY.
          </div>
        </div>

        <button
          class="plan-it-btn"
          :disabled="loading || !validEmployeeId"
          @click="runVoeDiscovery"
        >
          {{
            loadingAction === "discovery"
              ? "Discovering..."
              : "Test VOE Discovery"
          }}
        </button>

        <button
          class="plan-it-btn"
          :disabled="loading || !validEmployeeId"
          @click="runVoe"
        >
          {{ loadingAction === "voe" ? "Loading VOE..." : "Test Final VOE" }}
        </button>

        <button
          class="plan-it-btn"
          :disabled="!loaded || !prettyResult"
          @click="copyResponse"
        >
          {{ copied ? "Copied" : "Copy Response" }}
        </button>

        <button
          class="plan-it-btn secondary-btn"
          :disabled="loading || (!loaded && !error)"
          @click="clearResponse"
        >
          Clear
        </button>

        <div
          v-if="error"
          class="panel error-box"
        >
          <div class="error-title">
            Request Failed
          </div>

          <div>
            {{ error }}
          </div>
        </div>

      </div>

      <!-- RIGHT SIDE -->
      <div class="right-side-section">

        <div
          v-if="loaded"
          class="summary-bar"
        >
          <div class="summary-item">
            <div class="summary-label">Route</div>

            <div class="summary-value">
              {{ activeRoute || "—" }}
            </div>
          </div>

          <div class="summary-item">
            <div class="summary-label">Success</div>

            <div class="summary-value">
              {{ result?.success ? "YES" : "NO" }}
            </div>
          </div>

          <div class="summary-item">
            <div class="summary-label">Status</div>

            <div class="summary-value">
              {{ responseStatus }}
            </div>
          </div>

          <div class="summary-item">
            <div class="summary-label">Status Text</div>

            <div class="summary-value">
              {{ responseStatusText }}
            </div>
          </div>

          <div
            v-if="result?.employee?.payType"
            class="summary-item"
          >
            <div class="summary-label">Pay Type</div>

            <div class="summary-value">
              {{ result.employee.payType }}
            </div>
          </div>
        </div>

        <div
          v-if="loaded"
          class="sheet-scroll-container"
        >
          <div class="panel report-panel">
            <div class="block-title">
              Backend Response
            </div>

            <pre class="json-box">{{ prettyResult }}</pre>
          </div>
        </div>

        <div
          v-else
          class="panel hint-panel"
        >
          <div class="hint-title">
            UKG Route Tester
          </div>

          <div>
            Click <b>Test UKG API</b> to test the connection.
          </div>

          <div>
            Enter an employee ID to test the VOE discovery or production route.
          </div>
        </div>

      </div>

    </div>

  </div>
</template>

<script>
import axios from "axios";

export default {
  name: "UkgApiTest",

  data() {
    return {
      loading: false,
      loadingAction: null,
      loaded: false,
      copied: false,
      error: null,
      result: null,
      employeeId: "",
      activeRoute: "",
      httpStatus: null,
      httpStatusText: "",
    };
  },

  computed: {
    validEmployeeId() {
      return /^[A-Za-z0-9_-]+$/.test(this.employeeId);
    },

    prettyResult() {
      if (!this.result) return "";

      return JSON.stringify(this.result, null, 2);
    },

    responseStatus() {
      return (
        this.httpStatus ??
        this.result?.status ??
        "—"
      );
    },

    responseStatusText() {
      return (
        this.httpStatusText ||
        this.result?.statusText ||
        "—"
      );
    },
  },

  methods: {
    beginRequest(action, route) {
      if (this.loading) return false;

      this.loading = true;
      this.loadingAction = action;
      this.loaded = false;
      this.copied = false;
      this.error = null;
      this.result = null;
      this.activeRoute = route;
      this.httpStatus = null;
      this.httpStatusText = "";

      return true;
    },

    finishRequest() {
      this.loading = false;
      this.loadingAction = null;
    },

    handleSuccess(response) {
      this.result = response.data;
      this.loaded = true;
      this.httpStatus = response.status;
      this.httpStatusText = response.statusText;
    },

    handleError(err, fallbackMessage) {
      console.error("UKG route error:", err);

      this.result = err?.response?.data || {
        success: false,
        error: fallbackMessage,
      };

      this.loaded = true;
      this.httpStatus = err?.response?.status || null;
      this.httpStatusText = err?.response?.statusText || "";

      if (err?.response?.status === 403) {
        this.error =
          err?.response?.data?.error ||
          "This employee is not available through the HOURLY VOE route.";
        return;
      }

      if (err?.response?.status === 404) {
        this.error =
          err?.response?.data?.error ||
          "The requested UKG route or employee was not found.";
        return;
      }

      this.error =
        err?.response?.data?.error ||
        err?.response?.data?.details ||
        err?.message ||
        fallbackMessage;
    },

    validateEmployeeId() {
      if (!this.employeeId) {
        this.error = "Enter an employee ID.";
        return false;
      }

      if (!this.validEmployeeId) {
        this.error =
          "Employee ID may contain only letters, numbers, underscores, and hyphens.";
        return false;
      }

      return true;
    },

    async runTest() {
      const route = "/ukg/test";

      if (!this.beginRequest("connection", route)) return;

      try {
        const response = await axios.get(route);

        this.handleSuccess(response);
      } catch (err) {
        this.handleError(err, "Failed to test the UKG API.");
      } finally {
        this.finishRequest();
      }
    },

    async runEmployeeSample() {
      const route = "/ukg/employees/sample";

      if (!this.beginRequest("employees", route)) return;

      try {
        const response = await axios.get(route);

        this.handleSuccess(response);
      } catch (err) {
        this.handleError(
          err,
          "Failed to load the UKG employee sample."
        );
      } finally {
        this.finishRequest();
      }
    },

    async runVoeDiscovery() {
      this.error = null;

      if (!this.validateEmployeeId()) return;

      const encodedEmployeeId = encodeURIComponent(this.employeeId);
      const route = `/ukg/voe/discovery/${encodedEmployeeId}`;

      if (!this.beginRequest("discovery", route)) return;

      try {
        const response = await axios.get(route);

        this.handleSuccess(response);
      } catch (err) {
        this.handleError(
          err,
          "Failed to run UKG VOE discovery."
        );
      } finally {
        this.finishRequest();
      }
    },

    async runVoe() {
      this.error = null;

      if (!this.validateEmployeeId()) return;

      const encodedEmployeeId = encodeURIComponent(this.employeeId);
      const route = `/ukg/voe/${encodedEmployeeId}`;

      if (!this.beginRequest("voe", route)) return;

      try {
        const response = await axios.get(route);

        this.handleSuccess(response);
      } catch (err) {
        this.handleError(
          err,
          "Failed to retrieve the UKG VOE."
        );
      } finally {
        this.finishRequest();
      }
    },

    async copyResponse() {
      if (!this.prettyResult) return;

      try {
        await navigator.clipboard.writeText(this.prettyResult);

        this.copied = true;

        window.setTimeout(() => {
          this.copied = false;
        }, 1500);
      } catch (err) {
        console.error("Copy failed:", err);

        this.error = "Could not copy the response to the clipboard.";
      }
    },

    clearResponse() {
      this.loaded = false;
      this.copied = false;
      this.error = null;
      this.result = null;
      this.activeRoute = "";
      this.httpStatus = null;
      this.httpStatusText = "";
    },
  },
};
</script>

<style scoped>
.ukg-page {
  background-color: #0b0101;
  min-height: 100vh;
  padding: 14px;
}

.ukg-test-container {
  font-family: Garamond, serif;
  -webkit-font-smoothing: antialiased;
  -moz-osx-font-smoothing: grayscale;
  color: #cbbfbf;
  display: flex;
  flex-direction: column;
  align-items: stretch;
  background-color: #0b0101;
  min-height: 100vh;
}

/* Header */
.page-header-row {
  display: grid;
  grid-template-columns: 1fr auto 1fr;
  align-items: center;
  margin-bottom: 10px;
}

.page-heading {
  font-family: "Cormorant Garamond", serif;
  font-size: 36px;
  color: #cbbfbf;
  margin: 0;
  text-align: center;
}

/* Main layout */
.main-content {
  display: grid;
  grid-template-columns: 260px minmax(0, 1fr);
  gap: 12px;
  width: 100%;
  padding: 0 14px;
  box-sizing: border-box;
  align-items: start;
}

/* Left side */
.left-side-section {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 10px;
  margin-top: 40px;
}

/* Right side */
.right-side-section {
  width: 100%;
  min-width: 0;
}

/* Panel base */
.panel {
  background-color: #0b0101;
  border: 1px solid #888;
  border-radius: 10px;
  padding: 10px 12px;
  text-align: left;
  box-sizing: border-box;
}

.info-panel,
.employee-panel {
  width: 100%;
}

.panel-title {
  font-weight: 800;
  margin-bottom: 8px;
}

/* Employee input */
.field-label {
  display: block;
  color: #fff;
  font-size: 14px;
  font-weight: 700;
  margin-bottom: 5px;
}

.employee-input {
  width: 100%;
  height: 38px;
  border-radius: 8px;
  border: 1px solid #777;
  box-sizing: border-box;
  padding: 7px 10px;
  background-color: #111;
  color: #fff;
  font-family: Consolas, Monaco, monospace;
  font-size: 14px;
  outline: none;
}

.employee-input:focus {
  border-color: #cbbfbf;
  box-shadow: 0 0 0 2px rgba(203, 191, 191, 0.15);
}

.employee-input:disabled {
  opacity: 0.65;
  cursor: not-allowed;
}

.employee-help {
  margin-top: 8px;
}

/* Buttons */
.plan-it-btn {
  border-radius: 10px;
  padding: 8px 12px;
  width: 200px;
  background-color: #f3f4f6;
  border: 1px solid #cfd3d8;
  color: #333;
  font-weight: 600;
  cursor: pointer;
  transition:
    background-color 0.15s ease,
    border-color 0.15s ease;
  font-family: inherit;
  min-height: 38px;
}

.plan-it-btn:hover:not(:disabled) {
  background-color: #e5e7eb;
  border-color: #bfc5cc;
}

.plan-it-btn:disabled {
  background-color: #e5e7eb;
  color: #888;
  cursor: not-allowed;
}

.secondary-btn {
  background-color: #c7c9cc;
}

/* Summary bar */
.summary-bar {
  display: flex;
  justify-content: space-between;
  gap: 18px;
  padding: 10px 12px;
  border-radius: 10px;
  border: 1px solid #888;
  margin: 0 0 12px;
  background-color: #708090a4;
  flex-wrap: wrap;
  width: 100%;
  box-sizing: border-box;
}

.summary-item {
  min-width: 130px;
  flex: 1 1 130px;
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
}

.summary-label {
  font-size: 16px;
  opacity: 0.85;
  line-height: 1.1;
}

.summary-value {
  font-size: 17px;
  font-weight: bold;
  color: #fff;
  line-height: 1.1;
  margin-top: 4px;
  word-break: break-word;
}

/* Scroll container */
.sheet-scroll-container {
  max-height: 80vh;
  overflow-y: auto;
  width: 100%;
  border-radius: 10px;
  background-color: #0b0101;
  padding-right: 10px;
  box-sizing: border-box;
}

.sheet-scroll-container::-webkit-scrollbar {
  width: 10px;
}

.sheet-scroll-container::-webkit-scrollbar-thumb {
  background: #444;
  border-radius: 8px;
}

.sheet-scroll-container::-webkit-scrollbar-track {
  background: #111;
}

/* Report */
.report-panel {
  background-color: #708090a4;
  border-radius: 10px;
  border: 1px solid #888;
}

.block-title {
  font-weight: 800;
  font-size: 16px;
  color: #fff;
  margin-bottom: 8px;
}

.json-box {
  background-color: #111;
  color: #d8e6f3;
  border: 1px solid #333;
  border-radius: 10px;
  padding: 12px;
  min-height: 420px;
  max-height: 70vh;
  overflow: auto;
  white-space: pre-wrap;
  word-break: break-word;
  font-family: Consolas, Monaco, monospace;
  font-size: 13px;
  line-height: 1.35;
}

/* Hint and error */
.hint-panel {
  margin-top: 20px;
  background-color: #0b0101;
  line-height: 1.6;
}

.hint-title {
  font-size: 18px;
  font-weight: 800;
  color: #fff;
  margin-bottom: 8px;
}

.error-box {
  border-color: #aa4444;
  background: #2a0b0b;
  color: #fca5a5;
  width: 100%;
}

.error-title {
  font-weight: 800;
  color: #fff;
  margin-bottom: 5px;
}

.small-muted {
  font-size: 12px;
  opacity: 0.85;
  color: #fff;
}

/* Responsive layout */
@media (max-width: 800px) {
  .main-content {
    grid-template-columns: 1fr;
  }

  .left-side-section {
    margin-top: 0;
  }

  .plan-it-btn {
    width: 100%;
  }

  .page-heading {
    font-size: 28px;
  }
}

</style>